<template>
  <div>
    <div class="page-header">
      <div class="page-icon">📋</div>
      <h2>商品列表</h2>
    </div>

    <!-- 搜索框 -->
    <el-input
      v-model="searchQuery"
      placeholder="请输入商品名称或条形码进行搜索"
      prefix-icon="el-icon-search"
      clearable
      style="width: 300px; margin-bottom: 20px;"
    ></el-input>
<el-button
  type="success"
  icon="Download"
  @click="exportProducts"
  style="margin-bottom: 20px"
>
  批量导出商品
</el-button>

    <!-- 商品卡片列表 -->
    <el-row :gutter="20">
      <el-col :span="8" v-for="product in paginatedProducts" :key="product.product_id">
        <el-card :body-style="{ padding: '20px' }" class="box-card">
          <div>
            <h3>{{ product.product_name }}</h3>
            <p>条形码：{{ product.barcode }}</p>
            <p>类别：{{ product.category }}</p>
            <p>销售价格：¥{{ product.selling_price }}</p>
          </div>
          <div class="card-actions">
            <el-button type="primary" size="mini" @click="editProduct(product)">编辑</el-button>
            <el-button type="danger" size="mini" @click="deleteProduct(product)">删除</el-button>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 编辑商品弹窗 -->
    <el-dialog
      title="编辑商品"
      v-model="editDialogVisible"
      width="30%"
      :before-close="handleCloseEditDialog"
    >
      <el-form :model="editForm" :rules="editRules" ref="editFormRef" label-width="100px">
        <el-form-item label="商品名称" prop="product_name">
          <el-input v-model="editForm.product_name"></el-input>
        </el-form-item>
        <el-form-item label="条形码" prop="barcode">
          <el-input v-model="editForm.barcode"></el-input>
        </el-form-item>
        <el-form-item label="销售价格" prop="selling_price">
          <el-input v-model.number="editForm.selling_price" type="number"></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="handleCloseEditDialog">取消</el-button>
          <el-button type="primary" @click="submitEdit">确定</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 分页 -->
    <el-pagination
      v-show="totalItems > pageSize"
      :current-page="currentPage"
      :page-size="pageSize"
      :total="totalItems"
      layout="prev, pager, next, jumper, ->, total"
      @current-change="handlePageChange"
      style="margin-top: 20px; text-align: center;"
    ></el-pagination>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import API_BASE from '@/api';
import { ElMessageBox, ElMessage } from 'element-plus';

const products = ref([]);         // 所有商品列表
const searchQuery = ref('');      // 搜索框输入
const currentPage = ref(1);       // 当前页码
const pageSize = ref(17);         // 每页显示的商品数量
const totalItems = ref(0);        // 商品总数

// 编辑弹窗相关
const editDialogVisible = ref(false);
const editForm = ref({
  product_id: null,
  product_name: '',
  barcode: '',
  selling_price: null
});
const editRules = {
  product_name: [
    { required: true, message: '请输入商品名称', trigger: 'blur' }
  ],
  barcode: [
    { required: true, message: '请输入条形码', trigger: 'blur' }
  ],
  selling_price: [
    { required: true, message: '请输入销售价格', trigger: 'blur' },
    { type: 'number', min: 0, message: '销售价格必须为非负数', trigger: 'blur' }
  ]
};
const editFormRef = ref(null);
/**
 * 批量导出商品 Excel
 */
const exportProducts = () => {
  window.open(`${API_BASE}/api/products/export`);
};
// 获取商品列表
const getProducts = async () => {
  try {
    const response = await axios.get(`${API_BASE}/api/products`);
    if (response.data.success) {
      products.value = response.data.products;
      totalItems.value = response.data.products.length;  // 设置总商品数
    } else {
      ElMessage.error('获取商品列表失败：' + response.data.message);
    }
  } catch (error) {
    ElMessage.error('请求出错：' + error.message);
  }
};

// 编辑商品
const editProduct = (product) => {
  editForm.value = {
    product_id: product.product_id,
    product_name: product.product_name,
    barcode: product.barcode,
    selling_price: product.selling_price
  };
  editDialogVisible.value = true;
};

// 提交编辑
const submitEdit = async () => {
  editFormRef.value.validate(async (valid) => {
    if (valid) {
      try {
        const response = await axios.put(`${API_BASE}/api/products/${editForm.value.product_id}`, {
          product_name: editForm.value.product_name,
          barcode: editForm.value.barcode,
          selling_price: editForm.value.selling_price
        });
        if (response.data.success) {
          ElMessage.success('商品更新成功');
          editDialogVisible.value = false;
          await getProducts(); // 刷新商品列表
        } else {
          ElMessage.error('商品更新失败：' + response.data.message);
        }
      } catch (error) {
        ElMessage.error('请求出错：' + error.message);
      }
    } else {
      ElMessage.warning('请填写正确的商品信息');
    }
  });
};

// 关闭编辑弹窗
const handleCloseEditDialog = () => {
  editDialogVisible.value = false;
  editFormRef.value.resetFields();
};

// 删除商品
const deleteProduct = async (product) => {
  try {
    await ElMessageBox.confirm(
      '确定要删除该商品吗？',
      '提示',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }
    );

    const response = await axios.delete(`${API_BASE}/api/products/${product.product_id}`);
    if (response.data.success) {
      ElMessage.success('商品删除成功');
      getProducts();  // 刷新商品列表
    } else {
      ElMessage.error('商品删除失败：' + response.data.message);
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('请求出错：' + error.message);
    }
  }
};

// 过滤商品列表（搜索功能）
const filteredProducts = computed(() => {
  if (!searchQuery.value) return products.value;

  return products.value.filter(product => {
    return (
      product.product_name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      product.barcode.includes(searchQuery.value)
    );
  });
});

// 分页后的商品列表
const paginatedProducts = computed(() => {
  const startIndex = (currentPage.value - 1) * pageSize.value;
  const endIndex = currentPage.value * pageSize.value;
  return filteredProducts.value.slice(startIndex, endIndex);
});

// 处理页码变化
const handlePageChange = (page) => {
  currentPage.value = page;
};

// 初始化获取商品列表
onMounted(() => {
  getProducts();
});
</script>

<style scoped>
.box-card {
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  transition: transform 0.2s, box-shadow 0.2s;
  margin-bottom: 16px;
  border: 1px solid var(--border-lighter);
}

.box-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-md);
}

.box-card h3 {
  margin: 0 0 12px 0;
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 600;
}

.box-card p {
  margin: 6px 0;
  color: var(--text-secondary);
  font-size: 13px;
}

.box-card p strong {
  color: var(--text-tertiary);
  margin-right: 4px;
}

.card-actions {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.el-card {
  border: 1px solid var(--border-lighter);
}
</style>
